import { communityReviewPanelTallySchema, communityReviewPanelMembersSchema, communityReviewPanelMutationResponseSchema, communityReviewPanelAddRequestSchema, communityProjectCommentsSchema, communityProjectCommentRequestSchema, type CommunityReviewPanelTally, type CommunityReviewPanelMembers, type CommunityReviewPanelAddRequest, type CommunityProjectComments } from "@squeek/module-contracts/community-review-v1";
import {
  holderBadgeSchema, holderBadgesSchema, holderReviewSchema, holderOverrideResponseSchema,
  type HolderBadge, type HolderDraft, type HolderReview, type HolderOverride,
} from "@squeek/module-contracts/community-holders-v1";
import {
  communityAdminMemesResponseSchema,
  communityAdminMemeUploadersResponseSchema,
  communityAdminMemeRemoveResponseSchema,
  communityAdminMemeUploaderMutationResponseSchema,
  type CommunityAdminMemeUploaderMutationResponse,
  type CommunityAdminMemesResponse,
  type CommunityAdminMemeUploadersResponse,
  type CommunityAdminMemeRemoveResponse,
  communityAdminSpecialBadgesSchema,
  communityAdminSpecialBadgeResponseSchema,
  type CommunityAdminSpecialBadgeId,
  type CommunityAdminSpecialBadges,
  type CommunityAdminSpecialBadgeRequest,
  type CommunityAdminSpecialBadgeResponse,
  communityAdminClustersSchema,
  communityAdminCollectionSchema,
  communityAdminCollectionsSchema,
  communityAdminDecisionSchema,
  communityAdminDisqualificationResponseSchema,
  communityAdminInvitesResponseSchema,
  communityAdminFlagResponseSchema,
  communityAdminXApprovalResponseSchema,
  type CommunityAdminXApprovalResponse,
  type CommunityAdminApproveXRequest,
  type CommunityAdminRevokeXApprovalRequest,
  communityAdminTreeDisqualifyResponseSchema,
  communityAdminFunnelSchema,
  communityAdminGraphSchema,
  communityAdminJobsSchema,
  communityAdminOverviewSchema,
  communityAdminPeoplePageSchema,
  communityAdminPersonDetailSchema,
  communityAdminProjectSchema,
  communityAdminProjectReviewSchema,
  communityAdminProjectsPageSchema,
  communityAdminProjectCountsSchema,
  communityAdminProjectBatchResponseSchema,
  type CommunityAdminProjectBatchRequest,
  type CommunityAdminProjectBatchResponse,
  type CommunityAdminProjectCounts,
  type CommunityAdminReviewSignalFlag,
  communityAdminReferralTreeSchema,
  communityAdminRoleRuleSchema,
  communityAdminRoleRulesSchema,
  communityAdminRoundDetailSchema,
  communityAdminRoundExportSchema,
  communityAdminRoundSchema,
  communityAdminRoundsSchema,
  communityAdminSessionSchema,
  communityAdminSessionResponseSchema,
  communityAdminStreamConnectResponseSchema,
  communityAdminStreamStatusSchema,
  communityAdminWinnersResponseSchema,
  type CommunityAdminClusters,
  type CommunityAdminCollection,
  type CommunityAdminCollections,
  type CommunityAdminCreateCollectionRequest,
  type CommunityAdminCreateRoleRuleRequest,
  type CommunityAdminCreateRoundRequest,
  type CommunityAdminDecision,
  type CommunityAdminDecisionRequest,
  type CommunityAdminDisqualificationResponse,
  type CommunityAdminInvitesResponse,
  type CommunityAdminFlagResponse,
  type CommunityAdminTreeDisqualifyResponse,
  type CommunityAdminFreezeRequest,
  type CommunityAdminFunnel,
  type CommunityAdminGraph,
  type CommunityAdminJobs,
  type CommunityAdminOverview,
  type CommunityAdminPeoplePage,
  type CommunityAdminPersonDetail,
  type CommunityAdminProject,
  type CommunityAdminProjectReview,
  type CommunityAdminProjectsPage,
  type CommunityAdminProjectStatusFilter,
  type CommunityAdminReferralTree,
  type CommunityAdminRoleRule,
  type CommunityAdminRoleRules,
  type CommunityAdminRound,
  type CommunityAdminRoundDetail,
  type CommunityAdminRoundExport,
  type CommunityAdminRounds,
  type CommunityAdminSession,
  type CommunityAdminSessionResponse,
  type CommunityAdminVerifyRequest,
  type CommunityAdminStreamConnectResponse,
  type CommunityAdminStreamStatus,
  type CommunityAdminWinnersRequest,
  type CommunityAdminWinnersResponse,
  type CommunityAllowlistCriteria,
} from "@squeek/module-contracts/community-admin-v1";
import {
  communityChallengeResponseSchema,
  type CommunityChallengeRequest,
  type CommunityChallengeResponse,
} from "@squeek/module-contracts/community-v1";
import type { ZodType } from "zod";

/** The API answered with an error body `{ error }`. */
export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
    this.name = "AdminApiError";
  }
}

/** The request never produced a readable response; this is not evidence of lost authorization. */
export class AdminNetworkError extends Error {
  constructor(cause: unknown) {
    super("The community API could not be reached.", { cause });
    this.name = "AdminNetworkError";
  }
}

/** A disabled admin configuration is authorization loss, not a transient service outage. */
export function isAdminAuthLoss(error: unknown): error is AdminApiError {
  return error instanceof AdminApiError && (error.status === 401 || error.status === 403
    || (error.status === 503 && error.code === "admin_not_configured"));
}

export type PeopleQueryParams = Readonly<Record<string, string>>;

export interface CommunityAdminApi {
  memes(query: Readonly<Record<string, string>>): Promise<CommunityAdminMemesResponse>;
  memeUploaders(query: Readonly<Record<string, string>>): Promise<CommunityAdminMemeUploadersResponse>;
  grantMemeUploader(code: string, note?: string): Promise<CommunityAdminMemeUploaderMutationResponse>;
  revokeMemeUploader(personId: string): Promise<CommunityAdminMemeUploaderMutationResponse>;
  removeMeme(publicId: string, reason: string): Promise<CommunityAdminMemeRemoveResponse>;
  challenge(request: CommunityChallengeRequest): Promise<CommunityChallengeResponse>;
  verify(request: CommunityAdminVerifyRequest): Promise<CommunityAdminSessionResponse>;
  session(): Promise<CommunityAdminSession>;
  logout(): Promise<void>;
  overview(): Promise<CommunityAdminOverview>;
  people(query: PeopleQueryParams): Promise<CommunityAdminPeoplePage>;
  person(personId: string): Promise<CommunityAdminPersonDetail>;
  specialBadges(personId: string): Promise<CommunityAdminSpecialBadges>;
  setSpecialBadge(personId: string, badgeId: CommunityAdminSpecialBadgeId, request: CommunityAdminSpecialBadgeRequest): Promise<CommunityAdminSpecialBadgeResponse>;
  disqualify(personId: string, reason: string): Promise<CommunityAdminDisqualificationResponse>;
  requalify(personId: string): Promise<CommunityAdminDisqualificationResponse>;
  freezeInvites(personId: string, reason: string): Promise<CommunityAdminInvitesResponse>;
  unfreezeInvites(personId: string, reason: string): Promise<CommunityAdminInvitesResponse>;
  clearFlag(personId: string, reason: string): Promise<CommunityAdminFlagResponse>;
  approveX(personId: string, request: CommunityAdminApproveXRequest): Promise<CommunityAdminXApprovalResponse>;
  revokeXApproval(personId: string, request: CommunityAdminRevokeXApprovalRequest): Promise<CommunityAdminXApprovalResponse>;
  disqualifyTree(personId: string, reason: string, confirmCount?: number): Promise<CommunityAdminTreeDisqualifyResponse>;
  /** CODE projects, newest submission first. */
  projects(status: CommunityAdminProjectStatusFilter, cursor?: string, filters?: { q?: string; flags?: CommunityAdminReviewSignalFlag[] }): Promise<CommunityAdminProjectsPage>;
  projectCounts(): Promise<CommunityAdminProjectCounts>;
  projectPanel(projectId: string): Promise<CommunityReviewPanelTally>;
  reviewPanel(): Promise<CommunityReviewPanelMembers>;
  addReviewPanelMember(request: CommunityReviewPanelAddRequest): Promise<{ ok: true }>;
  removeReviewPanelMember(personId: string): Promise<{ ok: true }>;
  /** Internal team notes on a project (admin and review panel only). */
  projectComments(projectId: string): Promise<CommunityProjectComments>;
  addProjectComment(projectId: string, body: string): Promise<CommunityProjectComments>;
  deleteProjectComment(projectId: string, commentId: string): Promise<CommunityProjectComments>;
  declineProject(projectId: string, updatedAt: string, reason: string): Promise<CommunityAdminProject>;
  batchProjects(request: CommunityAdminProjectBatchRequest): Promise<CommunityAdminProjectBatchResponse>;
  /** Review signals of one project (domain, site, source, member, related projects); read-only. */
  projectReview(projectId: string): Promise<CommunityAdminProjectReview>;
  removeProject(projectId: string, reason: string): Promise<CommunityAdminProject>;
  restoreProject(projectId: string): Promise<CommunityAdminProject>;
  /** Lists a project publicly and grants Code reward eligibility; approving an IMD project verifies it. */
  approveProject(projectId: string, updatedAt: string, reason?: string): Promise<CommunityAdminProject>;
  unapproveProject(projectId: string, reason: string): Promise<CommunityAdminProject>;
  referralTree(): Promise<CommunityAdminReferralTree>;
  graph(): Promise<CommunityAdminGraph>;
  clusters(): Promise<CommunityAdminClusters>;
  funnel(days: number): Promise<CommunityAdminFunnel>;
  jobs(): Promise<CommunityAdminJobs>;
  holderBadges(): Promise<{ badges: HolderBadge[] }>;
  createHolderBadge(request: HolderDraft): Promise<HolderBadge>;
  updateHolderBadge(badgeId: string, request: HolderDraft): Promise<HolderBadge>;
  snapshotHolderBadge(badgeId: string): Promise<HolderBadge>;
  publishHolderBadge(badgeId: string): Promise<HolderBadge>;
  retireHolderBadge(badgeId: string): Promise<HolderBadge>;
  reviewHolderBadge(badgeId: string, cursor?: string): Promise<HolderReview>;
  overrideHolderBadge(badgeId: string, request: HolderOverride): Promise<{ ok: true }>;
  collections(): Promise<CommunityAdminCollections>;
  createCollection(request: CommunityAdminCreateCollectionRequest): Promise<CommunityAdminCollection>;
  deleteCollection(collectionId: string): Promise<void>;
  roleRules(): Promise<CommunityAdminRoleRules>;
  createRoleRule(request: CommunityAdminCreateRoleRuleRequest): Promise<CommunityAdminRoleRule>;
  deleteRoleRule(ruleId: string): Promise<void>;
  rounds(): Promise<CommunityAdminRounds>;
  createRound(request: CommunityAdminCreateRoundRequest): Promise<CommunityAdminRound>;
  round(roundId: string): Promise<CommunityAdminRoundDetail>;
  updateCriteria(roundId: string, criteria: CommunityAllowlistCriteria): Promise<CommunityAdminRound>;
  decide(roundId: string, request: CommunityAdminDecisionRequest): Promise<CommunityAdminDecision>;
  removeDecision(roundId: string, personId: string): Promise<void>;
  freeze(roundId: string, request: CommunityAdminFreezeRequest): Promise<CommunityAdminRound>;
  publish(roundId: string): Promise<CommunityAdminRound>;
  exportCsv(roundId: string): Promise<Blob>;
  /** Seats direct winners by wallet as manual includes on a draft round. */
  seatWinners(roundId: string, request: CommunityAdminWinnersRequest): Promise<CommunityAdminWinnersResponse>;
  /** A frozen round's wallets in the sale's proof-bundle input shape. */
  launchExport(roundId: string): Promise<CommunityAdminRoundExport>;
  streamStatus(): Promise<CommunityAdminStreamStatus>;
  /** Starts the owner connect; navigate (top level) to the returned authorize URL. */
  streamConnect(): Promise<CommunityAdminStreamConnectResponse>;
  registerStreamWebhook(): Promise<CommunityAdminStreamStatus>;
  revalidateStreamWebhook(): Promise<CommunityAdminStreamStatus>;
  createStreamSubscriptions(): Promise<CommunityAdminStreamStatus>;
  deleteStreamSubscriptions(): Promise<CommunityAdminStreamStatus>;
}

const ERROR_CODE = /^[a-z0-9_]{1,64}$/;

export interface AdminApiAuthorization {
  token(): string | null;
  onAuthLoss?(token: string, error: AdminApiError): void;
}

/** Credential-free bearer requests with schema validation and no automatic retries. */
export function createBearerTransport(
  root: string,
  authorization: AdminApiAuthorization,
  fetchImpl: typeof fetch = globalThis.fetch,
) {
  async function send(path: string, init: { method?: string; body?: unknown; anonymous?: boolean } = {}): Promise<Response> {
    const method = init.method ?? (init.body === undefined ? "GET" : "POST");
    const token = init.anonymous ? null : authorization.token();
    if (!init.anonymous && token === null) throw new AdminApiError(401, "unauthorized");
    let response: Response;
    try {
      response = await fetchImpl(`${root}${path}`, {
        method,
        credentials: "omit",
        headers: {
          ...(token === null ? {} : { authorization: `Bearer ${token}` }),
          ...(method === "GET" ? {} : { "content-type": "application/json" }),
        },
        ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
      });
    } catch (error) {
      throw new AdminNetworkError(error);
    }
    if (!response.ok) {
      let code = "request_failed";
      try {
        const body: unknown = await response.json();
        if (typeof body === "object" && body !== null && "error" in body && typeof body.error === "string"
          && ERROR_CODE.test(body.error)) code = body.error;
      } catch {
        // A non-JSON error body keeps the generic code.
      }
      const error = new AdminApiError(response.status, code);
      if (token !== null && isAdminAuthLoss(error)) authorization.onAuthLoss?.(token, error);
      throw error;
    }
    return response;
  }

  async function json<T>(schema: ZodType<T, unknown>, path: string, init?: { method?: string; body?: unknown; anonymous?: boolean }): Promise<T> {
    const response = await send(path, init);
    return schema.parse(await response.json());
  }
  return { send, json };
}

export function createCommunityAdminApi(
  baseUrl: string,
  authorization: AdminApiAuthorization,
  fetchImpl: typeof fetch = globalThis.fetch,
): CommunityAdminApi {
  const { send, json } = createBearerTransport(`${baseUrl}/v1/community/admin`, authorization, fetchImpl);

  const id = encodeURIComponent;

  return {
    projectPanel: (projectId) => json(communityReviewPanelTallySchema, `/projects/${id(projectId)}/panel`),
    reviewPanel: () => json(communityReviewPanelMembersSchema, "/review-panel"),
    addReviewPanelMember: (request) => json(communityReviewPanelMutationResponseSchema, "/review-panel", { body: communityReviewPanelAddRequestSchema.parse(request) }),
    removeReviewPanelMember: (personId) => json(communityReviewPanelMutationResponseSchema, `/review-panel/${id(personId)}`, { method: "DELETE" }),
    projectComments: (projectId) => json(communityProjectCommentsSchema, `/projects/${id(projectId)}/comments`),
    addProjectComment: (projectId, body) => json(communityProjectCommentsSchema, `/projects/${id(projectId)}/comments`, { body: communityProjectCommentRequestSchema.parse({ body }) }),
    deleteProjectComment: (projectId, commentId) => json(communityProjectCommentsSchema, `/projects/${id(projectId)}/comments/${id(commentId)}`, { method: "DELETE" }),
    memes: (query) => json(communityAdminMemesResponseSchema, `/memes?${new URLSearchParams(query)}`),
    memeUploaders: (query) => json(communityAdminMemeUploadersResponseSchema, `/memes/uploaders?${new URLSearchParams(query)}`),
    grantMemeUploader: (code, note) => json(communityAdminMemeUploaderMutationResponseSchema, "/memes/uploaders", { body: { code, ...(note ? { note } : {}) } }),
    revokeMemeUploader: (personId) => json(communityAdminMemeUploaderMutationResponseSchema, `/memes/uploaders/${id(personId)}`, { method: "DELETE" }),
    removeMeme: (publicId, reason) => json(communityAdminMemeRemoveResponseSchema, `/memes/${id(publicId)}/remove`, { body: { reason } }),
    challenge: (request) => json(communityChallengeResponseSchema, "/auth/challenge", { body: request, anonymous: true }),
    verify: (request) => json(communityAdminSessionResponseSchema, "/auth/verify", { body: request, anonymous: true }),
    session: () => json(communityAdminSessionSchema, "/auth/session"),
    logout: async () => { await send("/auth/logout", { method: "POST" }); },
    overview: () => json(communityAdminOverviewSchema, "/overview"),
    people: (query) => {
      const search = new URLSearchParams(Object.entries(query)).toString();
      return json(communityAdminPeoplePageSchema, `/people${search === "" ? "" : `?${search}`}`);
    },
    person: (personId) => json(communityAdminPersonDetailSchema, `/people/${id(personId)}`),
    specialBadges: (personId) => json(communityAdminSpecialBadgesSchema, `/people/${id(personId)}/special-badges`),
    setSpecialBadge: (personId, badgeId, request) =>
      json(communityAdminSpecialBadgeResponseSchema, `/people/${id(personId)}/special-badges/${id(badgeId)}`, { body: request }),
    disqualify: (personId, reason) =>
      json(communityAdminDisqualificationResponseSchema, `/people/${id(personId)}/disqualify`, { body: { reason } }),
    requalify: (personId) =>
      json(communityAdminDisqualificationResponseSchema, `/people/${id(personId)}/requalify`, { method: "POST" }),
    freezeInvites: (personId, reason) =>
      json(communityAdminInvitesResponseSchema, `/people/${id(personId)}/invites/freeze`, { body: { reason } }),
    unfreezeInvites: (personId, reason) =>
      json(communityAdminInvitesResponseSchema, `/people/${id(personId)}/invites/unfreeze`, { body: { reason } }),
    approveX: (personId, request) =>
      json(communityAdminXApprovalResponseSchema, `/people/${id(personId)}/x-approval`, { body: request }),
    revokeXApproval: (personId, request) =>
      json(communityAdminXApprovalResponseSchema, `/people/${id(personId)}/x-approval`, { method: "DELETE", body: request }),
    clearFlag: (personId, reason) =>
      json(communityAdminFlagResponseSchema, `/people/${id(personId)}/flag/clear`, { body: { reason } }),
    disqualifyTree: (personId, reason, confirmCount) =>
      json(communityAdminTreeDisqualifyResponseSchema, `/people/${id(personId)}/disqualify-tree`, {
        body: { reason, ...(confirmCount === undefined ? {} : { confirmCount }) },
      }),
    projectCounts: () => json(communityAdminProjectCountsSchema, "/projects/counts"),
    declineProject: (projectId, updatedAt, reason) =>
      json(communityAdminProjectSchema, `/projects/${id(projectId)}/decline`, { body: { updatedAt, reason } }),
    batchProjects: (request) => json(communityAdminProjectBatchResponseSchema, "/projects/batch", { body: request }),
    projects: (status, cursor, filters) => {
      const search = new URLSearchParams({ status, ...(cursor === undefined ? {} : { cursor }),
        ...(filters?.q ? { q: filters.q } : {}), ...(filters?.flags?.length ? { flags: filters.flags.join(",") } : {}) }).toString();
      return json(communityAdminProjectsPageSchema, `/projects?${search}`);
    },
    projectReview: (projectId) => json(communityAdminProjectReviewSchema, `/projects/${id(projectId)}/review`),
    removeProject: (projectId, reason) =>
      json(communityAdminProjectSchema, `/projects/${id(projectId)}/remove`, { body: { reason } }),
    restoreProject: (projectId) =>
      json(communityAdminProjectSchema, `/projects/${id(projectId)}/restore`, { method: "POST" }),
    approveProject: (projectId, updatedAt, reason) =>
      json(communityAdminProjectSchema, `/projects/${id(projectId)}/approve`, { body: { updatedAt, ...(reason === undefined ? {} : { reason }) } }),
    unapproveProject: (projectId, reason) =>
      json(communityAdminProjectSchema, `/projects/${id(projectId)}/unapprove`, { body: { reason } }),
    referralTree: () => json(communityAdminReferralTreeSchema, "/referrals/tree"),
    graph: () => json(communityAdminGraphSchema, "/graph"),
    clusters: () => json(communityAdminClustersSchema, "/clusters"),
    funnel: (days) => json(communityAdminFunnelSchema, `/funnel?days=${days}`),
    jobs: () => json(communityAdminJobsSchema, "/jobs"),
    holderBadges: () => json(holderBadgesSchema, "/holders"),
    createHolderBadge: (request) => json(holderBadgeSchema, "/holders", { body: request }),
    updateHolderBadge: (badgeId, request) => json(holderBadgeSchema, `/holders/${id(badgeId)}`, { method: "PUT", body: request }),
    snapshotHolderBadge: (badgeId) => json(holderBadgeSchema, `/holders/${id(badgeId)}/snapshot`, { method: "POST" }),
    publishHolderBadge: (badgeId) => json(holderBadgeSchema, `/holders/${id(badgeId)}/publish`, { method: "POST" }),
    retireHolderBadge: (badgeId) => json(holderBadgeSchema, `/holders/${id(badgeId)}/retire`, { method: "POST" }),
    reviewHolderBadge: (badgeId, cursor) => json(holderReviewSchema, `/holders/${id(badgeId)}/holders${cursor === undefined ? "" : `?cursor=${id(cursor)}`}`),
    overrideHolderBadge: (badgeId, request) => json(holderOverrideResponseSchema, `/holders/${id(badgeId)}/overrides`, { body: request }),
    collections: () => json(communityAdminCollectionsSchema, "/collections"),
    createCollection: (request) => json(communityAdminCollectionSchema, "/collections", { body: request }),
    deleteCollection: async (collectionId) => {
      await send(`/collections/${id(collectionId)}`, { method: "DELETE" });
    },
    roleRules: () => json(communityAdminRoleRulesSchema, "/discord/role-rules"),
    createRoleRule: (request) => json(communityAdminRoleRuleSchema, "/discord/role-rules", { body: request }),
    deleteRoleRule: async (ruleId) => {
      await send(`/discord/role-rules/${id(ruleId)}`, { method: "DELETE" });
    },
    rounds: () => json(communityAdminRoundsSchema, "/allowlist/rounds"),
    createRound: (request) => json(communityAdminRoundSchema, "/allowlist/rounds", { body: request }),
    round: (roundId) => json(communityAdminRoundDetailSchema, `/allowlist/rounds/${id(roundId)}`),
    updateCriteria: (roundId, criteria) =>
      json(communityAdminRoundSchema, `/allowlist/rounds/${id(roundId)}/criteria`, { method: "PUT", body: criteria }),
    decide: (roundId, request) =>
      json(communityAdminDecisionSchema, `/allowlist/rounds/${id(roundId)}/decisions`, { body: request }),
    removeDecision: async (roundId, personId) => {
      await send(`/allowlist/rounds/${id(roundId)}/decisions/${id(personId)}`, { method: "DELETE" });
    },
    freeze: (roundId, request) =>
      json(communityAdminRoundSchema, `/allowlist/rounds/${id(roundId)}/freeze`, { body: request }),
    publish: (roundId) => json(communityAdminRoundSchema, `/allowlist/rounds/${id(roundId)}/publish`, { method: "POST" }),
    exportCsv: async (roundId) => (await send(`/allowlist/rounds/${id(roundId)}/export.csv`)).blob(),
    seatWinners: (roundId, request) =>
      json(communityAdminWinnersResponseSchema, `/allowlist/rounds/${id(roundId)}/winners`, { body: request }),
    launchExport: (roundId) => json(communityAdminRoundExportSchema, `/allowlist/rounds/${id(roundId)}/launch-export`),
    streamStatus: () => json(communityAdminStreamStatusSchema, "/stream"),
    streamConnect: () => json(communityAdminStreamConnectResponseSchema, "/stream/connect", { method: "POST" }),
    registerStreamWebhook: () => json(communityAdminStreamStatusSchema, "/stream/webhook", { method: "POST" }),
    revalidateStreamWebhook: () => json(communityAdminStreamStatusSchema, "/stream/webhook", { method: "PUT" }),
    createStreamSubscriptions: () => json(communityAdminStreamStatusSchema, "/stream/subscriptions", { method: "POST" }),
    deleteStreamSubscriptions: () => json(communityAdminStreamStatusSchema, "/stream/subscriptions", { method: "DELETE" }),
  };
}

/** Human-readable text for an error shown next to the action that failed. */
export function errorMessage(error: unknown): string {
  if (error instanceof AdminApiError) return `${error.code.replaceAll("_", " ")} (${error.status})`;
  if (error instanceof Error) return error.message;
  return "Unexpected error";
}
